import { useEffect, useMemo, useState } from 'react';

import { Typography, Wrapper } from '@/components';
import { useTranslation } from 'react-i18next';
import { useDrafts } from '@/hooks/client';

import styles from './CampaignPageDrafts.module.css';
import { Draft } from './components/Draft';
import { Tabs } from './components/Tabs';
import { filteredTab } from './utils';

interface CampaignPageDraftsProps {
  campaignId: string;
}

export function CampaignPageDrafts({ campaignId }: CampaignPageDraftsProps) {
  const [currentTab, setCurrentTab] = useState('REVIEW');
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const { t } = useTranslation();

  const { data, isLoading, refetch } = useDrafts(campaignId);

  const memoDrafts = useMemo(() => (data?.drafts || []) as Api.Client.Draft[], [data]);
  const memoDraftsVersions = useMemo(
    () =>
      memoDrafts
        .filter((draft) => draft.latest_version != null)
        .map((draft) => draft.latest_version),
    [memoDrafts]
  );

  useEffect(() => {
    if (!isLoading && !hasLoadedOnce) {
      if (
        filteredTab(memoDraftsVersions, 'REVIEW').length ||
        (!filteredTab(memoDraftsVersions, 'APPROVED').length &&
          !filteredTab(memoDraftsVersions, 'REJECTED').length)
      ) {
        return undefined;
      }
      if (filteredTab(memoDraftsVersions, 'APPROVED').length) {
        setCurrentTab('APPROVED');
      } else if (filteredTab(memoDraftsVersions, 'REJECTED').length) {
        setCurrentTab('REJECTED');
      }

      setHasLoadedOnce(true);
    }
    return undefined;
  }, [isLoading, memoDraftsVersions, hasLoadedOnce]);

  const TABS = useMemo(
    () => [
      {
        count: filteredTab(memoDraftsVersions, 'REVIEW').length,
        value: t('campaignTab.pending'),
        key: 'REVIEW'
      },
      {
        count: filteredTab(memoDraftsVersions, 'APPROVED').length,
        value: t('campaignTab.approved'),
        key: 'APPROVED'
      },
      {
        count: filteredTab(memoDraftsVersions, 'REJECTED').length,
        value: t('campaignTab.declined'),
        key: 'REJECTED'
      }
    ],
    [memoDraftsVersions, t]
  );

  const filteredData = useMemo(() => {
    return memoDrafts.filter((draft) => draft.latest_version?.status === currentTab);
  }, [memoDrafts, currentTab]);

  return (
    <Wrapper
      heading={t('campaignPageDrafts.heading')}
      isLoading={isLoading}
      campaignId={campaignId}
    >
      <div className={styles.campaignPageDraftsNavigation}>
        <Tabs setCurrentTab={setCurrentTab} currentTab={currentTab} tabs={TABS} />
      </div>

      {filteredData.length > 0 ? (
        filteredData.map((draft) => (
          <Draft
            key={`draft-${draft.draft_id}`}
            refetch={refetch}
            draft={draft}
            campaignId={campaignId}
          />
        ))
      ) : (
        <div className={styles.campaignPageDraftsListEmpty}>
          <Typography sizes={['h4']}>{t('campaignPageDrafts.empty')}</Typography>
        </div>
      )}
    </Wrapper>
  );
}
